import { MovementPattern } from '../types'

/**
 * Returns a normalized jig displacement in pixels.
 * Positive = upward pop. Independent of swing angle.
 */
export function getJigDisplacement(
  t: number,
  pattern: MovementPattern,
  pauseAtTop: boolean,
  pauseDuration: number,
  jigAtPause: boolean,
  jigDistance: number,
  jigSpeedUp: number,
  jigPauseBetween: number,
  jigSpeedDown: number,
  swingAngle: number,
): number {
  if (!jigAtPause) return 0

  // Jig runs continuously when enabled — uses t directly for timing
  const jigElapsed = t

  // Quick snap up, slow drift down — like pulling a lure through water
  // Up speed: 1-10 maps to snappy pop duration (faster = shorter)
  const upDuration = 0.18 - jigSpeedUp * 0.012 // 0.06s to 0.17s — very fast snap
  const holdTop = 0.05 + jigPauseBetween // brief hold + user-configured pause at peak
  // Down speed: 1-10 maps to slow drift duration (slower = longer)
  const downDuration = 3.2 - jigSpeedDown * 0.24 // 0.8s to 3.0s — slow gravity drift
  const restBottom = 0.3 // settle at bottom before next pop
  // Secondary smaller pop
  const smallPopUp = upDuration * 0.7
  const smallDrift = downDuration * 0.5
  const restEnd = 0.25
  const totalCycle =
    upDuration +
    holdTop +
    downDuration +
    restBottom +
    smallPopUp +
    smallDrift +
    restEnd
  const freq = 1 / totalCycle

  // jigDistance 1-30 maps directly to pixels of vertical displacement
  const ampPixels = jigDistance * 4 // 4 to 120 pixels of vertical movement

  const fadeIn = Math.min(1, jigElapsed / 0.5)

  const p1 = upDuration / totalCycle
  const p2 = p1 + holdTop / totalCycle
  const p3 = p2 + downDuration / totalCycle
  const p4 = p3 + restBottom / totalCycle
  const p5 = p4 + smallPopUp / totalCycle
  const p6 = p5 + smallDrift / totalCycle
  const cycle = (jigElapsed * freq) % 1

  let jig: number
  if (cycle < p1) {
    // Sharp snap upward — fast ease-out
    const t = cycle / p1
    jig = 1 - (1 - t) * (1 - t) // quadratic ease-out (fast start)
  } else if (cycle < p2) {
    // Brief hold at peak
    jig = 1
  } else if (cycle < p3) {
    // Slow drift down — gentle ease-in (gravity through water)
    const t = (cycle - p2) / (p3 - p2)
    // Slow start, gradually accelerating like sinking through water
    jig = 1 - t * t * t // cubic ease — very slow initial descent
  } else if (cycle < p4) {
    // Rest at bottom
    jig = 0
  } else if (cycle < p5) {
    // Small secondary snap up
    const t = (cycle - p4) / (p5 - p4)
    jig = (1 - (1 - t) * (1 - t)) * 0.4
  } else if (cycle < p6) {
    // Small slow drift back down
    const t = (cycle - p5) / (p6 - p5)
    jig = 0.4 * (1 - t * t * t)
  } else {
    jig = 0
  }
  return jig * ampPixels * fadeIn
}

/**
 * Returns a normalized swing value from -1 to 1.
 * The WaterScene multiplies this by swingAngle to get degrees.
 * Jig is handled separately via getJigDisplacement.
 */
export function getArmatureSwing(
  pattern: MovementPattern,
  t: number,
  pauseAtTop: boolean,
  pauseDuration: number,
  swingAngle: number = 45,
  pauseAngle: number = 92,
  sweepJerkiness: number = 0,
): number {
  // Scale swing time with angle so angular velocity stays smooth at large angles
  const angleScale = Math.max(1, swingAngle / 45)

  // Easing function with jerkiness: 0 = pure sinusoidal, 10 = staircase with many micro-stops
  const ease = (p: number) => {
    const smooth = 0.5 - 0.5 * Math.cos(p * Math.PI)
    if (sweepJerkiness <= 0) return smooth
    // Number of discrete steps increases with jerkiness
    const steps = 2 + Math.round(sweepJerkiness * 2) // 2 to 22 steps
    // Quantize the smooth value into staircase steps
    const staircase = Math.round(smooth * steps) / steps
    // Blend between smooth and staircase based on jerkiness
    const blend = Math.min(1, sweepJerkiness / 5) // full staircase by jerkiness=5
    return smooth * (1 - blend) + staircase * blend
  }

  // Helper: swing with optional pause at configured pauseAngle
  function swingWithPauseAtPeaks(baseSwingTime: number): number {
    if (!pauseAtTop || pauseDuration <= 0) {
      // No pause — normal swing between -1 and +1
      const cycleDuration = baseSwingTime * 2
      const phase = ((t % cycleDuration) + cycleDuration) % cycleDuration
      if (phase < baseSwingTime) {
        const p = phase / baseSwingTime
        return -1 + 2 * ease(p)
      } else {
        const p = (phase - baseSwingTime) / baseSwingTime
        return 1 - 2 * ease(p)
      }
    }

    // With pause: swing full range (-1 to +1) but pause at ±pauseAngle point
    // pausePoint = where in the -1..+1 range the armature pauses
    const pausePoint = Math.min(pauseAngle, swingAngle) / swingAngle
    const pause = pauseDuration

    // Full cycle path: -1 → +pausePoint [PAUSE] → +1 → -pausePoint [PAUSE] → -1
    // Time for each segment proportional to distance traveled
    const dA = 1 + pausePoint // distance: -1 → +pausePoint
    const dB = 1 - pausePoint // distance: +pausePoint → +1
    const dC = 1 + pausePoint // distance: +1 → -pausePoint
    const dD = 1 - pausePoint // distance: -pausePoint → -1
    const totalDist = dA + dB + dC + dD // always = 4
    const tA = baseSwingTime * (dA / totalDist)
    const tB = baseSwingTime * (dB / totalDist)
    const tC = baseSwingTime * (dC / totalDist)
    const tD = baseSwingTime * (dD / totalDist)
    const cycleDuration = baseSwingTime + 2 * pause
    const phase = ((t % cycleDuration) + cycleDuration) % cycleDuration

    const e1 = tA // end of segment 1
    const e2 = tA + pause // end of pause 1
    const e3 = tA + pause + tB // end of segment 2
    const e4 = tA + pause + tB + tC // end of segment 3
    const e5 = tA + pause + tB + tC + pause // end of pause 2

    if (phase < e1) {
      // Segment 1: -1 → +pausePoint
      const p = phase / tA
      return -1 + dA * ease(p)
    } else if (phase < e2) {
      // Pause at +pausePoint
      return pausePoint
    } else if (phase < e3) {
      // Segment 2: +pausePoint → +1
      const p = (phase - e2) / tB
      return pausePoint + dB * ease(p)
    } else if (phase < e4) {
      // Segment 3: +1 → -pausePoint
      const p = (phase - e3) / tC
      return 1 - dC * ease(p)
    } else if (phase < e5) {
      // Pause at -pausePoint
      return -pausePoint
    } else {
      // Segment 4: -pausePoint → -1
      const p = (phase - e5) / tD
      return -pausePoint - dD * ease(p)
    }
  }

  switch (pattern) {
    case 'Gentle Sway':
      return swingWithPauseAtPeaks(1.7 * angleScale)

    case 'Slow Sweep':
      return swingWithPauseAtPeaks(3.0 * angleScale)

    default:
      return 0
  }
}
